import React, {useState, useMemo} from 'react';
import * as Yup from 'yup';
import {Formik} from 'formik';
import valid from 'card-validator';
import {
  Box,
  GridContainerWithTitle,
  HorizontalDivider,
  TransparentButton,
  Typography,
  Button,
  TextField,
  ArrowRightIcon,
  FrameworkImage,
  VisibilityBox,
} from '@ttx/design-system';

import {SelectCountryField} from './select-country-field';
import countries from './select-country-field/countries.json';
import {
  formatCreditCardDate,
  isAmericanExpressCard,
} from '../../../../utils/string-util';

export interface MultiDropFormProps {
  walletAddress?: string;
  email: string;
}

export interface PaymentDetailsFormProps {
  cardholderName: string;
  email: string;
  cardNumber: string;
  expiry: string;
  cvv: string;
  billingLine1: string;
  billingLine2?: string;
  country: string;
  city: string;
  state?: string;
  postCode: string;
  expiryMonth: string | null;
  expiryYear: string | null;
}

export interface CashPaymentDetailsModalProps {
  trackName?: string;
  artist?: string;
  imageSrc?: string;
  total?: string | null;
  transactionFee?: string | null;
  onSubmit: (finalValues: PaymentDetailsFormProps) => Promise<void>;
  onModalClose: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  onResetQuantity: (qty: number) => void;
  quantity: number;
  isMultiCopy?: boolean;
  isTraxxStemz?: boolean;
  isEmilG?: boolean;
}

export const CashPaymentDetailsModal: React.FC<
  CashPaymentDetailsModalProps
> = ({
  trackName,
  artist,
  imageSrc,
  total,
  transactionFee,
  onSubmit,
  onModalClose,
  onIncrement,
  onDecrement,
  onResetQuantity,
  quantity,
  isMultiCopy,
  isTraxxStemz = false,
  isEmilG = false,
}: CashPaymentDetailsModalProps) => {
  const validationSchema = Yup.object().shape({
    cardholderName: Yup.string()
      .test(
        'isValidCardholderName',
        'Name is not valid',
        (value) => (value?.split(' ') || []).length > 1,
      )
      .required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    cardNumber: Yup.string()
      .test(
        'isValidCardNumber',
        'Card number not valid',
        (value) => valid.number(value).isValid,
      )
      .test(
        'americanExpressCardNotAllowed',
        'Sorry, we don’t accept Amex',
        (value) => !isAmericanExpressCard(value),
      )
      .required('Required'),
    expiry: Yup.string()
      .test(
        'isValidExpiry',
        'Expiry not valid',
        (value) => valid.expirationDate(value).isValid,
      )
      .required('Required'),
    cvv: Yup.string()
      .test('isValidCVV', 'CVV not valid', (value) => valid.cvv(value).isValid)
      .required('Required'),
    billingLine1: Yup.string().required('Required'),
    billingLine2: Yup.string(),
    country: Yup.string()
      .oneOf(
        countries.map((country) => country.text),
        'Must be a valid country',
      )
      .required('Required'),
    city: Yup.string().required('Required'),
    state: Yup.string().when('country', {
      is: (country: string) =>
        country === 'Canada' || country === 'United States',
      then: Yup.string().required(
        'State is required. Use the two-letter code for the subdivision.',
      ),
    }),
    postCode: Yup.string()
      .required('Required')
      .test(
        'isValidPostcode',
        'Postcode not valid',
        (value) => valid.postalCode(value).isValid,
      ),
  });

  const initialValues = {
    cardholderName: '',
    email: '',
    cardNumber: '',
    expiry: '',
    cvv: '',
    billingLine1: '',
    billingLine2: '',
    country: '',
    city: '',
    state: '',
    postCode: '',
    expiryMonth: '',
    expiryYear: '',
  };

  const [isNorthAmerica, setIsNorthAmerica] = useState(false);

  const _onSubmit = async (values: PaymentDetailsFormProps) => {
    await onSubmit({
      ...values,
      expiryMonth: valid.expirationDate(values.expiry).month,
      expiryYear: valid.expirationDate(values.expiry).year,
      country:
        countries.find((country) => country.text === values.country)?.value ||
        '',
    });
  };

  const QtyButton = ({
    onPress,
    label,
  }: {
    onPress: () => void;
    label: string;
  }) => {
    return (
      <TransparentButton onPress={onPress}>
        <Box
          height={['twelve', 'eight']}
          width={['twelve', 'eight']}
          justifyContent="center"
          alignItems="center"
        >
          <Typography textStyle="s" color="textDefault" textAlign="center">
            {label}
          </Typography>
        </Box>
      </TransparentButton>
    );
  };

  const incrementButton = useMemo(() => {
    return <QtyButton onPress={onIncrement} label="+" />;
  }, [onIncrement]);

  const decrementButton = useMemo(() => {
    return <QtyButton onPress={onDecrement} label="-" />;
  }, [onDecrement]);

  const quantityRendered = useMemo(() => {
    return (
      <Typography textStyle="s" color="textDefault">
        {quantity}
      </Typography>
    );
  }, [quantity]);

  return (
    <GridContainerWithTitle
      title="Payment details"
      onModalClose={onModalClose}
      hasBottomBorder={false}
      maxWidth={656}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={_onSubmit}
        validateOnChange
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          values,
          isSubmitting,
          setFieldValue,
          touched,
          errors,
        }) => {
          return (
            <Box>
              <Box mt="ten" marginX="lg" mb="six">
                <Box flex="1" flexDirection={['column', 'row']}>
                  <Box flex="1" mr={['zero', 'five']}>
                    <TextField
                      label="Cardholder name*"
                      placeholder="Full name"
                      onChangeText={handleChange('cardholderName')}
                      onBlur={handleBlur('cardholderName')}
                      value={values.cardholderName}
                      error={
                        errors.cardholderName && touched.cardholderName
                          ? errors.cardholderName
                          : undefined
                      }
                    />
                  </Box>
                  <Box flex="1" ml={['zero', 'five']}>
                    <TextField
                      label="Email*"
                      placeholder="mail@example.com"
                      onChangeText={handleChange('email')}
                      onBlur={handleBlur('email')}
                      value={values.email}
                      error={
                        errors.email && touched.email ? errors.email : undefined
                      }
                    />
                  </Box>
                </Box>
                <Box marginY={['zero', 'nine']}>
                  <Box flex="1" flexDirection={['column', 'row']}>
                    <Box flex="1" mr={['zero', 'five']}>
                      <TextField
                        label="Card number*"
                        placeholder="&#8226;&#8226;&#8226;&#8226; &#8226;&#8226;&#8226;&#8226; &#8226;&#8226;&#8226;&#8226; &#8226;&#8226;&#8226;&#8226;"
                        onChangeText={handleChange('cardNumber')}
                        onBlur={handleBlur('cardNumber')}
                        value={values.cardNumber}
                        error={
                          errors.cardNumber && touched.cardNumber
                            ? errors.cardNumber
                            : undefined
                        }
                      />
                    </Box>
                    <Box flex="1" ml={['zero', 'five']} flexDirection="row">
                      <Box flex="1" mr="two">
                        <TextField
                          label="Valid until*"
                          placeholder="MM/YY"
                          onChangeText={handleChange('expiry')}
                          onBlur={(e) => {
                            setFieldValue(
                              'expiry',
                              formatCreditCardDate(e.target.value),
                            );
                            handleBlur('expiry');
                          }}
                          value={values.expiry}
                          error={
                            errors.expiry && touched.expiry
                              ? errors.expiry
                              : undefined
                          }
                        />
                      </Box>
                      <Box flex="1" ml="two">
                        <TextField
                          placeholder=""
                          label="CVV*"
                          onChangeText={handleChange('cvv')}
                          onBlur={handleBlur('cvv')}
                          value={values.cvv}
                          error={
                            errors.cvv && touched.cvv ? errors.cvv : undefined
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box flex="1" flexDirection={['column', 'row']} mt="one">
                    <Box flex="1" mr={['zero', 'five']}>
                      <TextField
                        label="Billing address line 1*"
                        placeholder="Address line 1"
                        onChangeText={handleChange('billingLine1')}
                        onBlur={handleBlur('billingLine1')}
                        value={values.billingLine1}
                        error={
                          errors.billingLine1 && touched.billingLine1
                            ? errors.billingLine1
                            : undefined
                        }
                      />
                    </Box>
                    <Box flex="1" ml={['zero', 'five']}>
                      <TextField
                        label="Billing address line 2"
                        placeholder="Address line 2"
                        onChangeText={handleChange('billingLine2')}
                        onBlur={handleBlur('billingLine2')}
                        value={values.billingLine2}
                        error={
                          errors.billingLine2 && touched.billingLine2
                            ? errors.billingLine2
                            : undefined
                        }
                      />
                    </Box>
                  </Box>
                </Box>
                <Box
                  flex="1"
                  flexDirection={['column', 'row']}
                  mt="one"
                  zIndex={1}
                >
                  <Box flex="1" mr={['zero', 'five']} zIndex={2}>
                    <SelectCountryField
                      setFieldValue={setFieldValue}
                      setIsNorthAmerica={setIsNorthAmerica}
                      onBlur={handleBlur('country')}
                      countrySelected={values.country}
                      error={
                        errors.country && touched.country
                          ? errors.country
                          : undefined
                      }
                    />
                  </Box>
                  <Box flex="1" ml={['zero', 'five']}>
                    <TextField
                      label="City*"
                      placeholder="City"
                      onChangeText={handleChange('city')}
                      onBlur={handleBlur('city')}
                      value={values.city}
                      error={
                        errors.city && touched.city ? errors.city : undefined
                      }
                    />
                  </Box>
                </Box>
                <Box flex="1" flexDirection="row" mt="one">
                  <Box flex="1" mr="five">
                    <TextField
                      label="State or county"
                      placeholder={isNorthAmerica ? '(required)' : '(optional)'}
                      onChangeText={handleChange('state')}
                      onBlur={handleBlur('state')}
                      value={values.state}
                      error={
                        errors.state && touched.state ? errors.state : undefined
                      }
                    />
                  </Box>
                  <Box flex="1" ml="five">
                    <TextField
                      label="Postal/zip code"
                      placeholder=""
                      onChangeText={handleChange('postCode')}
                      onBlur={handleBlur('postCode')}
                      value={values.postCode}
                      error={
                        errors.postCode && touched.postCode
                          ? errors.postCode
                          : undefined
                      }
                    />
                  </Box>
                </Box>
              </Box>
              <HorizontalDivider />
              <Box flexDirection={['column', 'row']}>
                <Box flexDirection="row" flex="1">
                  <Box width={[78, 72]} height={[78, 72]}>
                    <FrameworkImage
                      src={imageSrc || ''}
                      alt={trackName || ''}
                      layout="fill"
                      objectFit="cover"
                    />
                  </Box>
                  <Box flex={1} m="md" justifyContent="center">
                    <Typography
                      textStyle="s"
                      color="textLow"
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                      overflow="hidden"
                    >
                      {artist}
                    </Typography>
                    <Typography
                      textStyle="s"
                      color="textDefault"
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                      overflow="hidden"
                    >
                      {trackName}
                    </Typography>
                  </Box>
                </Box>
                <VisibilityBox show={[true, false]}>
                  <HorizontalDivider />
                </VisibilityBox>
                <VisibilityBox
                  show={!!isMultiCopy || !!isTraxxStemz || !!isEmilG}
                >
                  <Box
                    alignItems="center"
                    justifyContent={['space-between', 'center']}
                    flexDirection="row"
                    p={['zero', 'md']}
                  >
                    <Box pr="four" flexDirection="row" pl="lg">
                      <Typography
                        textStyle="s"
                        color={['textDefault', 'textLow']}
                      >
                        Qty
                      </Typography>
                      <VisibilityBox show={[true, false]}>
                        <Box ml="twelve">{quantityRendered}</Box>
                      </VisibilityBox>
                    </Box>
                    <Box
                      flexDirection="row"
                      borderColor="outlines"
                      borderWidth={['zero', 'one']}
                      borderStyle="solid"
                    >
                      <Box
                        borderColor="outlines"
                        borderWidth="one"
                        borderStyle="solid"
                        borderTopWidth="zero"
                        borderBottomWidth="zero"
                        borderLeftWidth={['one', 'zero']}
                      >
                        {decrementButton}
                      </Box>
                      <VisibilityBox show={[false, true]}>
                        <Box
                          height="eight"
                          minWidth="eight"
                          justifyContent="center"
                          alignItems="center"
                          borderColor="outlines"
                          borderWidth="one"
                          borderStyle="solid"
                          borderTopWidth="zero"
                          borderBottomWidth="zero"
                          borderLeftWidth="zero"
                        >
                          {quantityRendered}
                        </Box>
                      </VisibilityBox>
                      {incrementButton}
                    </Box>
                  </Box>
                  <VisibilityBox show={[true, false]}>
                    <HorizontalDivider />
                  </VisibilityBox>
                </VisibilityBox>
                <Box flexDirection="row" mr={['zero', 'five']}>
                  <Box m="lg" justifyContent="center">
                    <Typography textStyle="s" color="textLow">
                      Service Fee
                    </Typography>
                    <Typography textStyle="s" color="textDefault">
                      {transactionFee}
                    </Typography>
                  </Box>
                  <Box m="lg" ml={['twelve', 'lg']} justifyContent="center">
                    <Typography textStyle="s" color="textLow">
                      Total
                    </Typography>
                    <Typography textStyle="s" color="textDefault">
                      {total}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <HorizontalDivider />
              <Button
                type="primary"
                disabled={isSubmitting}
                onPress={handleSubmit}
                buttonStyle={{margin: '8px 6px 8px 6px'}}
                leftAccessory={<ArrowRightIcon color="surfaceDark" />}
              >
                Purchase
              </Button>
              <HorizontalDivider />
            </Box>
          );
        }}
      </Formik>
    </GridContainerWithTitle>
  );
};
